import type { StudioClip, WorkerGridGuess, WorkerPackMeta, WorkerRequest, WorkerResponse } from "./protocol";
import { getOpsHash, opsSource } from "./opsSource";

// §9.17/D124 — Main-Thread-Fassade über dem Pyodide-Worker. Lazy-Singleton (der teure
// Boot startet erst beim Öffnen eines Edit-Modus, nie beim App-Start), strikt
// SERIALISIERTE Requests (eine Promise-Kette — der Worker ist single-threaded),
// Transferables für Zero-Copy. „Abbruch" = Last-Write-Wins im Aufrufer (Slider-Tuning:
// veraltete Ergebnisse werden verworfen; Einzelbild-Keys sind ohnehin schnell).

// Volumen-Deckel für die Rekey-Kette (D124): Peak ≈ 2× Frame-Volumen im WASM-Heap
// (chroma_key_frames hält Quelle+Ergebnis). 100 MPx ≈ 400 MB RGBA ⇒ ~800 MB Peak —
// konservativ unter den üblichen wasm32-Grenzen. Darüber: Edit verweigern (klare
// Meldung statt OOM-Crash); Streaming-Zwei-Pass ist der §13-Ausbau.
export const MAX_REKEY_TOTAL_PIXELS = 100_000_000;

export interface BrowserOpsClient {
  /** Bootet Worker+Pyodide+ops.py (idempotent). onBootProgress meldet die Lade-Stufen. */
  ensureReady(onBootProgress?: (stage: string) => void): Promise<{ opsHash: string }>;

  // ── D139 Studio: generischer Puffer `_st_frames` + Op-Stack ──
  /** Frames in den persistenten Studio-Puffer streamen (Video-Decode = async, Sheet/Bild = Array). */
  studioLoad(frames: AsyncIterable<ImageData> | Iterable<ImageData>, onProgress?: (done: number) => void): Promise<number>;
  /** ops.detect_grid auf einem Bild (Spritesheet-Erkennung, D139) — reine Abfrage, kein Puffer. */
  studioDetectGrid(img: ImageData): Promise<WorkerGridGuess>;
  /** Geladenes Sheet (Puffer[0]) → Frames entpacken, ERSETZT den Puffer (ops.spritesheet_unpack). */
  studioUnpack(cols: number, rows: number, frameCount: number): Promise<number>;
  /** Clip-Stack (Effekte PRO Clip) NICHT-mutierend anwenden → rohe Frames (Vorschau-Playback).
   *  `cropTransparent` = globales transparency_crop nach dem Padden (wenn ein Clip gekeyt ist). */
  studioPreview(clips: StudioClip[], seamPng?: Uint8Array, cropTransparent?: boolean): Promise<ImageData[]>;
  /** Clip-Stack → gepacktes Spritesheet-PNG + Meta (Sheet-Save). */
  studioPackSheet(
    clips: StudioClip[],
    fps: number,
    loop: boolean,
    seamPng?: Uint8Array,
    cropTransparent?: boolean,
  ): Promise<{ png: Uint8Array; meta: WorkerPackMeta }>;
  /** Clip-Stack → Einzelframe-PNG am Index (Einzelframe-Save). */
  studioFramePng(
    clips: StudioClip[],
    pos: number | { fraction: number },
    seamPng?: Uint8Array,
    cropTransparent?: boolean,
  ): Promise<Uint8Array>;
  /** Studio-Puffer freigeben (Studio verlassen) — WASM-Heap nicht dauerbelegen. */
  studioRelease(): Promise<void>;
}

let singleton: BrowserOpsClient | null = null;

export function getBrowserOps(): BrowserOpsClient {
  singleton ??= createClient();
  return singleton;
}

function createClient(): BrowserOpsClient {
  let worker: Worker | null = null;
  let nextId = 1;
  // Serialisierung: jede Anfrage hängt sich an die Kette — der Worker sieht nie zwei
  // überlappende Requests (Pyodide-Globals sind shared state).
  let chain: Promise<unknown> = Promise.resolve();
  const pending = new Map<number, { resolve: (r: WorkerResponse) => void }>();
  const bootListeners = new Set<(stage: string) => void>();
  let ready: Promise<{ opsHash: string }> | null = null;

  /** Codex-P1-C (D124-R1): Worker-Crash/Load-Failure darf KEINE Promises hängen lassen —
   *  alle pending Requests scharf fehlschlagen, Worker verwerfen und den Boot-Cache
   *  zurücksetzen (ein transienter Pyodide-/CDN-Fehler ist damit ohne Reload retrybar). */
  function failAll(reason: string): void {
    for (const [id, p] of pending) p.resolve({ id, ok: false, error: reason });
    pending.clear();
    worker?.terminate();
    worker = null;
    ready = null;
  }

  function ensureWorker(): Worker {
    if (worker) return worker;
    worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (ev: MessageEvent<WorkerResponse>) => {
      const msg = ev.data;
      if (msg.id === -1) {
        if (msg.ok && msg.type === "boot-progress") for (const fn of bootListeners) fn(msg.stage);
        return;
      }
      pending.get(msg.id)?.resolve(msg);
      pending.delete(msg.id);
    };
    worker.onerror = (ev) => failAll(`Browser-Engine-Fehler: ${ev.message || "Worker-Crash"}`);
    worker.onmessageerror = () => failAll("Browser-Engine-Fehler: Nachricht nicht deserialisierbar");
    return worker;
  }

  // Omit über einer Union muss DISTRIBUTIV sein (nacktes Omit kollabiert auf gemeinsame Keys).
  type Req = WorkerRequest extends infer T ? (T extends { id: number } ? Omit<T, "id"> : never) : never;

  function request(msg: Req, transfer: Transferable[] = []): Promise<WorkerResponse> {
    const w = ensureWorker();
    const id = nextId++;
    const p = new Promise<WorkerResponse>((resolve) => {
      pending.set(id, { resolve });
      w.postMessage({ ...msg, id } as WorkerRequest, transfer);
    });
    return p.then((res) => {
      if (!res.ok) throw new Error(res.error);
      return res;
    });
  }

  /** In die serielle Kette einreihen (Fehler eines Vorgängers reißen Nachfolger nicht mit). */
  function enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const next = chain.then(fn, fn);
    chain = next.catch(() => undefined);
    return next;
  }

  return {
    ensureReady(onBootProgress) {
      if (onBootProgress) bootListeners.add(onBootProgress);
      // Fehlgeschlagenen Boot NICHT cachen (Codex-P1-C): ready zurücksetzen, damit der
      // nächste Versuch (z.B. CDN wieder erreichbar) frisch booten kann.
      ready ??= enqueue(async () => {
        await request({ type: "init", opsSource });
        return { opsHash: await getOpsHash() };
      }).catch((e: unknown) => {
        ready = null;
        throw e;
      });
      return ready.finally(() => {
        if (onBootProgress) bootListeners.delete(onBootProgress);
      });
    },

    // ── D139 Studio ──
    studioLoad(frames, onProgress) {
      return enqueue(async () => {
        await request({ type: "st-begin" });
        try {
          let done = 0;
          let pixels = 0;
          for await (const frame of frames) {
            pixels += frame.width * frame.height;
            if (pixels > MAX_REKEY_TOTAL_PIXELS) {
              throw new Error(
                "Sequenz zu groß für die Browser-Bearbeitung (Speicher-Limit) — bitte kürzere/kleinere Quelle.",
              );
            }
            const buf = frame.data.slice().buffer as ArrayBuffer;
            await request({ type: "st-frame", width: frame.width, height: frame.height, data: buf }, [buf]);
            done += 1;
            onProgress?.(done);
          }
          if (done === 0) throw new Error("keine Frames geladen");
          return done;
        } catch (e) {
          await request({ type: "st-begin" }).catch(() => undefined); // teil-geladenen Puffer freigeben
          throw e;
        }
      });
    },

    studioDetectGrid(img) {
      return enqueue(async () => {
        const buf = img.data.slice().buffer as ArrayBuffer;
        const res = await request({ type: "st-detect-grid", width: img.width, height: img.height, data: buf }, [buf]);
        if (res.ok && res.type === "st-detect-grid") return res.grid;
        throw new Error("unerwartete Worker-Antwort");
      });
    },

    studioUnpack(cols, rows, frameCount) {
      return enqueue(async () => {
        const res = await request({ type: "st-unpack", cols, rows, frameCount });
        if (res.ok && res.type === "st-unpack") return res.count;
        throw new Error("unerwartete Worker-Antwort");
      });
    },

    studioPreview(clips, seamPng, cropTransparent) {
      return enqueue(async () => {
        const seamBuf = seamPng ? (seamPng.slice().buffer as ArrayBuffer) : undefined;
        const base = { type: "st-apply" as const, clips, emit: "frames" as const, cropTransparent: !!cropTransparent };
        const res = await request(seamBuf ? { ...base, seam: true, seamPng: seamBuf } : base, seamBuf ? [seamBuf] : []);
        if (res.ok && res.type === "st-frames") {
          return res.frames.map((b) => new ImageData(new Uint8ClampedArray(b), res.width, res.height));
        }
        throw new Error("unerwartete Worker-Antwort");
      });
    },

    studioPackSheet(clips, fps, loop, seamPng, cropTransparent) {
      return enqueue(async () => {
        const seamBuf = seamPng ? (seamPng.slice().buffer as ArrayBuffer) : undefined;
        const base = { type: "st-apply" as const, clips, emit: "sheet-png" as const, fps, loop, cropTransparent: !!cropTransparent };
        const res = await request(seamBuf ? { ...base, seam: true, seamPng: seamBuf } : base, seamBuf ? [seamBuf] : []);
        if (res.ok && res.type === "st-sheet") return { png: new Uint8Array(res.png), meta: res.meta };
        throw new Error("unerwartete Worker-Antwort");
      });
    },

    studioFramePng(clips, pos, seamPng, cropTransparent) {
      return enqueue(async () => {
        const seamBuf = seamPng ? (seamPng.slice().buffer as ArrayBuffer) : undefined;
        const posMsg = typeof pos === "number" ? { emitIndex: pos } : { emitFraction: pos.fraction };
        const base = { type: "st-apply" as const, clips, emit: "frame-png" as const, cropTransparent: !!cropTransparent, ...posMsg };
        const res = await request(seamBuf ? { ...base, seam: true, seamPng: seamBuf } : base, seamBuf ? [seamBuf] : []);
        if (res.ok && res.type === "st-frame-png") return new Uint8Array(res.png);
        throw new Error("unerwartete Worker-Antwort");
      });
    },

    studioRelease() {
      return enqueue(async () => {
        if (!worker) return;
        await request({ type: "st-release" });
      });
    },
  };
}
